import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Play, MessageCircle } from "lucide-react";
import { waLink, STATS } from "@/data/site";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.15 } },
};
const lineVar = {
  hidden: { y: "115%" },
  show: { y: 0, transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};

const Line = ({ children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span variants={lineVar} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "34%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0 will-change-transform">
        <img
          src="https://images.unsplash.com/photo-1548095115-45697e222a58?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="Cine en casa"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bp-bg via-bp-bg/90 to-bp-bg/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-bp-bg via-transparent to-bp-bg/60" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12 pt-28 pb-20 w-full"
      >
        <div className="max-w-3xl">
          <motion.span
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-bp-primary font-semibold mb-6"
          >
            <span className="w-8 h-px bg-bp-primary" /> Entretenimiento sin límites
          </motion.span>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-heading text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight leading-[1.04]"
          >
            <Line>
              Disfruta{" "}
              <span className="bg-gradient-to-r from-bp-primary via-[#9B5BFF] to-bp-accent bg-clip-text text-transparent">
                televisión en vivo
              </span>
              ,
            </Line>
            <Line>películas y series</Line>
            <Line>desde cualquier dispositivo.</Line>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={0.9}
            initial="hidden"
            animate="show"
            className="mt-6 text-base lg:text-lg text-[#A3A3A3] leading-relaxed max-w-xl"
          >
            Más de <strong className="text-white font-semibold">13,000 canales en vivo</strong> y miles de títulos bajo demanda. Activación rápida y soporte por WhatsApp.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={1.05}
            initial="hidden"
            animate="show"
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={waLink("Hola, quiero una prueba gratuita de BrothersPlus TV.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-trial-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 font-semibold text-black transition-all duration-300 hover:bg-[#1ebe5a] active:scale-95 hover:shadow-[0_0_28px_rgba(37,211,102,0.5)]"
            >
              <MessageCircle className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" /> Prueba gratuita
            </a>
            <a
              href="#planes"
              data-testid="hero-plans-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-bp-primary px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-bp-primaryHover active:scale-95 hover:shadow-[0_0_28px_rgba(46,125,255,0.55)]"
            >
              <Play className="w-4 h-4 fill-white transition-transform duration-300 group-hover:scale-110" /> Ver planes
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={1.25}
            initial="hidden"
            animate="show"
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4"
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                data-testid="hero-stat"
                className="glass rounded-xl px-4 py-3 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="font-heading text-xl lg:text-2xl font-semibold text-white">{s.value}</div>
                <div className="text-xs text-[#737373] mt-0.5">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#737373]">Scroll</span>
        <span className="relative h-10 w-px overflow-hidden bg-white/15">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-x-0 top-0 h-1/2 bg-bp-primary"
          />
        </span>
      </motion.div>
    </section>
  );
};
